import { useState } from 'react';
import type { CharacterBenefits } from '../../../hooks/useCharacterBenefits';
import type { PlaySession, CustomEffect } from '../../../stores/playSessionStore';
import type { ConditionName } from '@character-creator/types';
import { CONDITION_NAMES } from '@character-creator/types';
import { getCondition } from '@character-creator/data';
import { getBardicInspirationDie } from '@character-creator/core';
import { HPRing } from '../HPRing';
import { BardicInspirationIcon, DmInspirationIcon, CustomEffectIcon } from '../PlayModeIcons';
import { CustomEffectModal } from '../CustomEffectModal';

interface HealingResult {
  die: string;
  roll: number;
  conMod: number;
  total: number;
}

interface CombatLeftPanelProps {
  benefits: CharacterBenefits;
  session: PlaySession | null;
  currentHp: number;
  maxHp: number;
  tempHp: number;
  isDying: boolean;
  healingResult: HealingResult | null;
  onDamage: () => void;
  onHeal: () => void;
  onAddTempHp: () => void;
  onDeathSaveSuccess: () => void;
  onDeathSaveFailure: () => void;
  onResetDeathSaves: () => void;
  onUseHitDie: (dieType: string) => number | null;
  onUseResource: (name: string, amount?: number) => void;
  onRestoreResource: (name: string, amount?: number) => void;
  onToggleCondition: (condition: ConditionName) => void;
  onSetHealingResult: (result: HealingResult | null) => void;
  healingTimeoutRef: React.MutableRefObject<NodeJS.Timeout | null>;
  bardicInspiration: boolean;
  dmInspiration: boolean;
  bardLevel: number;
  customEffects: CustomEffect[];
  onToggleBardicInspiration: () => void;
  onToggleDmInspiration: () => void;
  onAddCustomEffect: (name: string, description: string) => void;
  onRemoveCustomEffect: (id: string) => void;
}

export function CombatLeftPanel({
  benefits,
  session,
  currentHp,
  maxHp,
  tempHp,
  isDying,
  healingResult,
  onDamage,
  onHeal,
  onAddTempHp,
  onDeathSaveSuccess,
  onDeathSaveFailure,
  onResetDeathSaves,
  onUseHitDie,
  onUseResource,
  onRestoreResource,
  onToggleCondition,
  onSetHealingResult,
  healingTimeoutRef,
  bardicInspiration,
  dmInspiration,
  bardLevel,
  customEffects,
  onToggleBardicInspiration,
  onToggleDmInspiration,
  onAddCustomEffect,
  onRemoveCustomEffect,
}: CombatLeftPanelProps) {
  const [expandedCondition, setExpandedCondition] = useState<ConditionName | null>(null);
  const [expandedEffect, setExpandedEffect] = useState<string | null>(null);
  const [showEffectModal, setShowEffectModal] = useState(false);

  return (
    <>
      {/* Healing Roll Result Popup (for hit dice usage) */}
      {healingResult && (
        <div
          className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50
            bg-background-card border-2 border-success rounded-lg p-6 shadow-2xl
            animate-in fade-in zoom-in duration-200"
        >
          <button
            onClick={() => onSetHealingResult(null)}
            className="absolute top-2 right-2 text-text-muted hover:text-text-primary"
          >
            ×
          </button>
          <div className="text-center">
            <div className="text-xs text-text-muted uppercase tracking-wider mb-1">
              Hit Dice ({healingResult.die})
            </div>
            <div className="font-mono text-5xl font-bold text-success mb-2">
              +{healingResult.total}
            </div>
            <div className="text-sm text-text-muted mb-2">HP healed</div>
            <div className="text-xs text-text-muted font-mono bg-background-primary px-3 py-1 rounded">
              {healingResult.die}: {healingResult.roll} + CON: {healingResult.conMod >= 0 ? '+' : ''}{healingResult.conMod}
            </div>
          </div>
        </div>
      )}

      {/* Left Panel - HP & Resources */}
      <div className="space-y-4">
        {/* HP Section */}
        <div className="bg-background-card rounded-lg p-4">
          <HPRing
            currentHp={currentHp}
            maxHp={maxHp}
            tempHp={tempHp}
            size="md"
            onDamage={onDamage}
            onHeal={onHeal}
          />

          {/* Temp HP Controls */}
          <div className="mt-3 flex justify-center">
            <button
              onClick={onAddTempHp}
              className="text-xs text-text-muted hover:text-class-wizard transition-colors"
            >
              + Add Temp HP
            </button>
          </div>

          {/* Death Saves (shown when dying) */}
          {isDying && (
            <div className="mt-4 p-3 bg-error/10 border border-error/30 rounded-lg">
              <div className="text-center text-sm text-error font-display mb-3">DEATH SAVES</div>
              <div className="flex justify-around">
                <div>
                  <div className="text-xs text-text-muted mb-1">Successes</div>
                  <div className="flex gap-1">
                    {[0, 1, 2].map((i) => (
                      <button
                        key={i}
                        onClick={onDeathSaveSuccess}
                        className={`w-5 h-5 rounded-full border-2 transition-colors ${
                          (session?.deathSaveSuccesses ?? 0) > i
                            ? 'bg-success border-success'
                            : 'border-text-muted hover:border-success'
                        }`}
                      />
                    ))}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-text-muted mb-1">Failures</div>
                  <div className="flex gap-1">
                    {[0, 1, 2].map((i) => (
                      <button
                        key={i}
                        onClick={onDeathSaveFailure}
                        className={`w-5 h-5 rounded-full border-2 transition-colors ${
                          (session?.deathSaveFailures ?? 0) > i
                            ? 'bg-error border-error'
                            : 'border-text-muted hover:border-error'
                        }`}
                      />
                    ))}
                  </div>
                </div>
              </div>
              <button
                onClick={onResetDeathSaves}
                className="mt-2 w-full text-xs text-text-muted hover:text-text-secondary"
              >
                Reset
              </button>
            </div>
          )}
        </div>

        {/* Resources Section */}
        <div className="bg-background-card rounded-lg p-4">
          <div className="font-display text-xs text-accent-gold uppercase tracking-wider mb-3">
            Resources
          </div>

          {/* Hit Dice */}
          {benefits.hitDice.map(({ die, count }) => (
            <div key={die} className="flex items-center justify-between py-2 border-b border-background-elevated">
              <span className="text-sm text-text-secondary">Hit Dice ({die})</span>
              <div className="flex gap-1">
                {Array.from({ length: count }).map((_, i) => {
                  const used = session?.hitDice[die]?.used ?? 0;
                  const isUsed = i < used;
                  return (
                    <button
                      key={i}
                      onClick={() => {
                        if (!isUsed) {
                          const roll = onUseHitDie(die);
                          if (roll !== null) {
                            const conMod = benefits.modifiers.constitution;
                            const healing = Math.max(1, roll + conMod);
                            onSetHealingResult({ die, roll, conMod, total: healing });
                            // Auto-clear after 3 seconds
                            if (healingTimeoutRef.current) clearTimeout(healingTimeoutRef.current);
                            healingTimeoutRef.current = setTimeout(() => onSetHealingResult(null), 3000);
                          }
                        }
                      }}
                      className={`w-4 h-4 rounded border-2 transition-colors ${
                        isUsed
                          ? 'border-text-muted bg-transparent'
                          : 'border-accent-gold bg-accent-gold hover:bg-accent-gold-light'
                      }`}
                    />
                  );
                })}
              </div>
            </div>
          ))}

          {/* Class Resources */}
          {Object.entries(session?.classResources ?? {}).map(([name, resource]) => (
            <div key={name} className="flex items-center justify-between py-2 border-b border-background-elevated">
              <span className="text-sm text-text-secondary">{name}</span>
              <div className="flex gap-1">
                {Array.from({ length: resource.max }).map((_, i) => {
                  const isUsed = i < resource.used;
                  return (
                    <button
                      key={i}
                      onClick={() => (isUsed ? onRestoreResource(name) : onUseResource(name))}
                      className={`w-4 h-4 rounded border-2 transition-colors ${
                        isUsed
                          ? 'border-text-muted bg-transparent'
                          : 'border-class-sorcerer bg-class-sorcerer hover:brightness-110'
                      }`}
                    />
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Conditions Section */}
        <div className="bg-background-card rounded-lg p-4">
          <div className="font-display text-xs text-accent-gold uppercase tracking-wider mb-3">
            Conditions
          </div>
          <div className="flex flex-wrap gap-1.5">
            {CONDITION_NAMES.filter((c) => c !== 'exhaustion').map((condition) => {
              const isActive = session?.activeConditions.includes(condition);
              return (
                <div key={condition} className="flex items-center gap-0.5">
                  <button
                    onClick={() => onToggleCondition(condition)}
                    className={`px-2 py-0.5 text-xs rounded-full border transition-colors ${
                      isActive
                        ? 'bg-error/30 border-error text-text-primary'
                        : 'bg-background-elevated border-background-elevated text-text-muted hover:border-text-muted'
                    }`}
                  >
                    {condition}
                  </button>
                  {isActive && (
                    <button
                      onClick={() => setExpandedCondition(expandedCondition === condition ? null : condition)}
                      className="w-4 h-4 flex items-center justify-center text-[9px] text-error/60
                        hover:text-error rounded-full border border-error/30 hover:bg-error/10 transition-colors"
                      title="Show details"
                    >
                      i
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          {/* Expanded Condition Description */}
          {expandedCondition && (() => {
            const conditionData = getCondition(expandedCondition);
            if (!conditionData) return null;
            return (
              <div className="mt-2 px-2 py-1.5 bg-error/5 border-l-2 border-error/30 rounded-r text-xs">
                <p className="text-text-secondary mb-1">{conditionData.description}</p>
                <ul className="list-disc list-inside text-text-muted space-y-0.5">
                  {conditionData.effects.map((effect, i) => (
                    <li key={i}>{effect}</li>
                  ))}
                </ul>
              </div>
            );
          })()}

          {/* Exhaustion Level */}
          <div className="mt-3 flex items-center justify-between">
            <span className="text-sm text-text-secondary">Exhaustion</span>
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5, 6].map((level) => (
                <div
                  key={level}
                  className={`w-4 h-4 rounded text-[10px] flex items-center justify-center ${
                    (session?.exhaustionLevel ?? 0) >= level
                      ? 'bg-error text-white'
                      : 'bg-background-elevated text-text-muted'
                  }`}
                >
                  {level}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Inspirations Section */}
        <div className="bg-background-card rounded-lg p-4">
          <div className="font-display text-xs text-accent-gold uppercase tracking-wider mb-3">
            Inspirations
          </div>
          <div className="flex flex-wrap gap-1.5">
            <button
              onClick={onToggleBardicInspiration}
              className={`flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-full border transition-colors ${
                bardicInspiration
                  ? 'bg-accent-gold/30 border-accent-gold text-text-primary'
                  : 'bg-background-elevated border-background-elevated text-text-muted hover:border-text-muted'
              }`}
            >
              <BardicInspirationIcon className="w-3.5 h-3.5" />
              Bardic{bardLevel > 0 ? ` ${getBardicInspirationDie(bardLevel)}` : ''}
            </button>
            <button
              onClick={onToggleDmInspiration}
              className={`flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-full border transition-colors ${
                dmInspiration
                  ? 'bg-accent-gold/30 border-accent-gold text-text-primary'
                  : 'bg-background-elevated border-background-elevated text-text-muted hover:border-text-muted'
              }`}
            >
              <DmInspirationIcon className="w-3.5 h-3.5" />
              Inspired
            </button>
          </div>
        </div>

        {/* Custom Effects Section */}
        <div className="bg-background-card rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="font-display text-xs text-accent-gold uppercase tracking-wider">
              Effects
            </div>
            <button
              onClick={() => setShowEffectModal(true)}
              className="text-xs text-accent-bronze hover:text-accent-gold transition-colors"
            >
              + Effect
            </button>
          </div>
          {customEffects.length > 0 ? (
            <div className="space-y-1">
              {customEffects.map((effect) => {
                const isExpanded = expandedEffect === effect.id;
                return (
                  <div key={effect.id}>
                    <div className="flex items-center gap-2 px-2 py-1 bg-class-wizard/10 border border-class-wizard/30 rounded">
                      <CustomEffectIcon className="w-3.5 h-3.5 text-class-wizard flex-shrink-0" />
                      <button
                        onClick={() => effect.description && setExpandedEffect(isExpanded ? null : effect.id)}
                        className="text-xs text-class-wizard font-medium flex-1 text-left truncate"
                      >
                        {effect.name}
                      </button>
                      <button
                        onClick={() => onRemoveCustomEffect(effect.id)}
                        className="text-class-wizard/60 hover:text-class-wizard text-xs transition-colors flex-shrink-0"
                      >
                        ×
                      </button>
                    </div>
                    {isExpanded && effect.description && (
                      <div className="mx-2 mt-1 px-2 py-1.5 bg-class-wizard/5 border-l-2 border-class-wizard/30 rounded-r text-xs text-text-muted">
                        {effect.description}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-xs text-text-muted text-center py-1">No active effects</div>
          )}
        </div>
      </div>

      <CustomEffectModal
        isOpen={showEffectModal}
        onClose={() => setShowEffectModal(false)}
        onAdd={onAddCustomEffect}
      />
    </>
  );
}
